import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Space, Spin, Table, Tabs, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { downloadFileAsBlob } from '../../utils/downloadFile';

export const IMAGE_EXT = /\.(jpe?g|png|webp|gif)$/i;
export const VIDEO_EXT = /\.(mp4|mov|webm)$/i;
export const PDF_EXT = /\.pdf$/i;
export const SHEET_EXT = /\.(xlsx|xls|csv)$/i;

export type PreviewKind = 'image' | 'video' | 'pdf' | 'sheet';

/** 这个附件能不能在弹窗里预览：图片、视频、PDF、表格（Excel/CSV）可以，Word 等其他格式不行。 */
export function previewKindOf(nameOrUrl: string): PreviewKind | null {
  const text = String(nameOrUrl || '').split('?')[0];
  if (IMAGE_EXT.test(text)) return 'image';
  if (VIDEO_EXT.test(text)) return 'video';
  if (PDF_EXT.test(text)) return 'pdf';
  if (SHEET_EXT.test(text)) return 'sheet';
  return null;
}

export interface MediaPreviewTarget {
  url: string;
  name: string;
}

interface Props {
  target: MediaPreviewTarget | null;
  onClose: () => void;
}

interface SheetData {
  name: string;
  columns: any[];
  rows: any[];
  truncated: boolean;
}

/** 表格最多显示的行数，避免超大文件卡住页面；完整内容可以下载查看 */
const MAX_ROWS = 2000;

/** 读取 Excel/CSV：每个工作表转成"第一行当表头"的表格数据。 */
async function loadSheets(url: string, name: string): Promise<SheetData[]> {
  const XLSX: any = await import('xlsx');
  const resp = await fetch(url, { credentials: 'include' as RequestCredentials });
  if (!resp.ok) throw new Error('文件加载失败');
  const isCsv = /\.csv$/i.test(name || url);
  const workbook = isCsv
    ? XLSX.read(await resp.text(), { type: 'string' })
    : XLSX.read(await resp.arrayBuffer(), { type: 'array' });
  return (workbook.SheetNames as string[]).map((sheetName: string) => {
    const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: '' }) as any[][];
    const width = Math.max(1, ...matrix.map(row => (Array.isArray(row) ? row.length : 0)));
    const header = Array.from({ length: width }, (_, i) => {
      const value = matrix[0]?.[i];
      return value === undefined || value === null || String(value).trim() === '' ? `列${i + 1}` : String(value);
    });
    const body = matrix.slice(1);
    const rows = body.slice(0, MAX_ROWS).map((row, index) => {
      const item: any = { __idx: index + 1 };
      header.forEach((_, i) => { item[`c${i}`] = String(row?.[i] ?? ''); });
      return item;
    });
    const columns = [
      { title: '#', dataIndex: '__idx', width: 60, render: (v: number) => <span style={{ color: '#999' }}>{v}</span> },
      ...header.map((title, i) => ({
        title, dataIndex: `c${i}`, width: 160,
        onCell: () => ({ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }),
      })),
    ];
    return { name: sheetName, columns, rows, truncated: body.length > MAX_ROWS };
  });
}

/** 附件弹窗预览：图片、视频、PDF、Excel/CSV 表格；并提供下载和新窗口打开。 */
const MediaPreviewModal: React.FC<Props> = ({ target, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState('');
  const kind = useMemo(() => (target ? previewKindOf(target.name || target.url) || previewKindOf(target.url) : null), [target]);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // 关闭预览时立刻停掉视频并释放它的网络连接：弹窗要等关闭动画结束才会销毁内容，这之前声音不能继续
  useEffect(() => {
    if (target) return undefined;
    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch {
        // 播放器已经被移除时忽略
      }
    }
    return undefined;
  }, [target]);

  useEffect(() => {
    setError('');
    setPdfUrl('');
    setSheets([]);
    setActiveSheet('');
    if (!target || (kind !== 'pdf' && kind !== 'sheet')) return undefined;
    let cancelled = false;
    let objectUrl = '';
    setLoading(true);
    (async () => {
      try {
        if (kind === 'pdf') {
          const resp = await fetch(target.url, { credentials: 'include' as RequestCredentials });
          if (!resp.ok) throw new Error('PDF 加载失败');
          objectUrl = URL.createObjectURL(await resp.blob());
          if (!cancelled) setPdfUrl(objectUrl);
        } else {
          const loaded = await loadSheets(target.url, target.name || target.url);
          if (!cancelled) {
            setSheets(loaded);
            setActiveSheet(loaded[0]?.name || '');
          }
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || '预览失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch { /* ignore */ } }
    };
  }, [target, kind]);

  const download = async () => {
    if (!target) return;
    setDownloading(true);
    try {
      await downloadFileAsBlob(target.url, target.name);
    } catch (err: any) {
      message.error(err?.message || '下载失败');
    } finally {
      setDownloading(false);
    }
  };

  const currentSheet = sheets.find(s => s.name === activeSheet) || sheets[0];

  const body = (() => {
    if (!target) return null;
    if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
    if (error) return <div style={{ color: '#cf1322' }}>{error}。可以点下面的“下载”或“在新窗口打开”查看原文件。</div>;
    if (kind === 'video') {
      return (
        <video ref={videoRef} key={target.url} src={target.url} controls preload="metadata" style={{ width: '100%', maxHeight: '70vh', background: '#000', borderRadius: 4 }} />
      );
    }
    if (kind === 'pdf') {
      return pdfUrl ? <iframe title="pdf-preview" src={pdfUrl} style={{ width: '100%', height: '70vh', border: 0 }} /> : null;
    }
    if (kind === 'sheet') {
      if (!currentSheet) return <div style={{ color: '#8c8c8c' }}>表格里没有内容</div>;
      return (
        <div>
          {sheets.length > 1 ? (
            <Tabs
              size="small"
              activeKey={currentSheet.name}
              onChange={setActiveSheet}
              items={sheets.map(sheet => ({ key: sheet.name, label: <span style={{ fontFamily: 'monospace' }}>{sheet.name}</span> }))}
            />
          ) : null}
          <Table
            size="small"
            rowKey="__idx"
            dataSource={currentSheet.rows}
            columns={currentSheet.columns}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100] as any,
              showTotal: (total: number, range: [number, number]) => `共 ${total} 条记录 显示 ${range[0]}-${range[1]} 条`,
            }}
            scroll={{ x: 'max-content', y: 480 }}
            tableLayout="auto"
          />
          {currentSheet.truncated ? (
            <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>只显示前 {MAX_ROWS} 行，完整内容请下载查看。</div>
          ) : null}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <img alt={target.name} src={target.url} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
      </div>
    );
  })();

  return (
    <Modal
      open={!!target}
      onCancel={onClose}
      // 关闭时销毁内容：视频元素随之卸载，不会在后台继续播放
      destroyOnClose
      footer={null}
      width={kind === 'sheet' || kind === 'pdf' ? 1100 : 960}
      title={<span style={{ color: '#1677ff', fontWeight: 700 }}>{target?.name || '附件预览'}</span>}
    >
      {target ? (
        <div>
          {body}
          <Space style={{ marginTop: 12 }} wrap>
            <Button icon={<DownloadOutlined />} loading={downloading} onClick={download}>下载</Button>
            <Button onClick={() => window.open(target.url, '_blank')}>在新窗口打开</Button>
            {kind === 'video' ? (
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                如果无法播放（例如 iPhone 拍摄的 HEVC 编码 .mov），请下载后用本地播放器打开。
              </span>
            ) : null}
          </Space>
        </div>
      ) : null}
    </Modal>
  );
};

export default MediaPreviewModal;
